import { z } from 'zod'

/**
 * Output contract for DeckGen generation. The model returns this shape (enforced via
 * generateObject); the store maps it onto SlideElement[] (adding ids, zIndex, defaults).
 * Kept tiny and client-safe so it can be shared by the store and the server route without
 * dragging the large system prompt into the client bundle.
 */

export const GENERATION_MODELS = [
  // Sonnet 5 — newest Anthropic flagship (1M context, adaptive thinking). No '/' and not a 'gemini' id, so
  // getModel() in the routes passes it straight to createAnthropic() untouched.
  'claude-sonnet-5',
  'claude-sonnet-4-5',
  'claude-sonnet-4-6',
  // Real dated Anthropic id so getModel() can pass it straight through (matches deckgen-verify-api).
  'claude-haiku-4-5-20251001',
  'claude-opus-4-6',
  'claude-opus-4-7',
  'claude-opus-4-8',
  'gemini-3.1-pro-preview',
  'gemini-3.5-flash',
  // Gemini 3.5 Flash-Lite (fastest/cheapest 3.5) + Gemini 3.6 Flash (newest Flash, multimodal). No '/' and
  // 'gemini'-prefixed, so getModel() routes them to createGoogleGenerativeAI() automatically. (Google ships
  // no "3.6 Flash-Lite" — the 3.6 line is Flash-only; the Lite is 3.5.)
  'gemini-3.5-flash-lite',
  'gemini-3.6-flash',
  // Gemini 3.7 Flash — newest Flash (1M in / 64k out, takes text+image+video+audio+PDF). Same 'gemini'
  // prefix, so getModel() routes it to createGoogleGenerativeAI() with no new branch. Thinking is
  // low/medium/high; 'minimal' is NOT supported and errors, but we only ever send 'low' here.
  'gemini-3.7-flash',
  // Gemini 3.8 Flash — released 2026-09; exact id verified against the models endpoint (no -preview
  // suffix). Same 'gemini' prefix, so it routes to Google with no new branch. Generation-only until the
  // chat enum + a backend deploy exist for it.
  'gemini-3.8-flash',
  // Ids containing '/' are routed through OpenRouter (see the getModel switch in the routes).
  'deepseek/deepseek-v4-pro',
  'deepseek/deepseek-v4-flash',
  // DeepSeek V4.1 Flash (2026-09). Unlike V4 Pro and V4 Flash it accepts images (OpenRouter
  // input_modalities text + image), so it is deliberately NOT in TEXT_ONLY_GENERATION_MODELS below.
  'deepseek/deepseek-v4.1-flash',
  'qwen/qwen3.7-plus',
  'qwen/qwen3.7-max',
  'moonshotai/kimi-k2.6',
  // Kimi K3 — Moonshot's newest flagship (1M context, accepts images), served via OpenRouter.
  'moonshotai/kimi-k3',
  'openai/gpt-5.5',
  // OpenAI GPT-5.6 family via OpenRouter ('/' → OpenRouter branch). Direct OpenAI 404s these on our account,
  // but OpenRouter serves them — same funded path as GPT-5.5. Sol = frontier, Terra + Luna = siblings.
  'openai/gpt-5.6-sol',
  'openai/gpt-5.6-terra',
  'openai/gpt-5.6-luna',
  // xAI Grok via OpenRouter (the '/' routes it through the OpenRouter branch). Direct xAI geo-blocks
  // Grok for our region, so we proxy it through OpenRouter like the other non-Anthropic/Google models.
  // 4.6 is the newer flagship; both are 500k context and take text + image + file, and both are heavy
  // silent reasoners — see the isReasoningModel branch in deckgen-flow-api (verified live 2026-08-12:
  // 4.6 spent 186 reasoning tokens answering "reply with exactly: OK").
  'x-ai/grok-4.6',
  'x-ai/grok-4.5',
] as const

export type GenerationModel = (typeof GENERATION_MODELS)[number]

export const GENERATION_MODEL_LABELS: Record<GenerationModel, string> = {
  'claude-sonnet-5': 'Sonnet 5',
  'claude-sonnet-4-5': 'Sonnet 4.5',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-haiku-4-5-20251001': 'Haiku 4.5',
  'claude-opus-4-6': 'Opus 4.6',
  'claude-opus-4-7': 'Opus 4.7',
  'claude-opus-4-8': 'Opus 4.8',
  'gemini-3.1-pro-preview': 'Gemini 3.1 Pro',
  'gemini-3.5-flash': 'Gemini 3.5 Flash',
  'gemini-3.5-flash-lite': 'Gemini 3.5 Flash Lite',
  'gemini-3.6-flash': 'Gemini 3.6 Flash',
  'gemini-3.7-flash': 'Gemini 3.7 Flash',
  'gemini-3.8-flash': 'Gemini 3.8 Flash',
  'deepseek/deepseek-v4-pro': 'DeepSeek V4 Pro',
  'deepseek/deepseek-v4-flash': 'DeepSeek V4 Flash',
  'deepseek/deepseek-v4.1-flash': 'DeepSeek V4.1 Flash',
  'qwen/qwen3.7-plus': 'Qwen 3.7 Plus',
  'qwen/qwen3.7-max': 'Qwen 3.7 Max',
  'moonshotai/kimi-k2.6': 'Kimi K2.6',
  'moonshotai/kimi-k3': 'Kimi K3',
  'openai/gpt-5.5': 'GPT-5.5',
  'openai/gpt-5.6-sol': 'GPT-5.6 Sol',
  'openai/gpt-5.6-terra': 'GPT-5.6 Terra',
  'openai/gpt-5.6-luna': 'GPT-5.6 Luna',
  'x-ai/grok-4.6': 'Grok 4.6',
  'x-ai/grok-4.5': 'Grok 4.5',
}

export const DEFAULT_GENERATION_MODEL: GenerationModel = 'claude-sonnet-5'

/**
 * Generation models that cannot take image inputs (per OpenRouter's input_modalities). Sending an
 * image part to them is a hard request error, so vision payloads (style-reference screenshots,
 * reproduce-this-slide images) must be stripped before the call. All Claude/Gemini models and the
 * remaining OpenRouter ones (qwen3.7-plus, kimi-k2.6, openai/gpt-5.5) accept images, as does the
 */
const TEXT_ONLY_GENERATION_MODELS: ReadonlySet<GenerationModel> = new Set([
  'deepseek/deepseek-v4-pro',
  'deepseek/deepseek-v4-flash',
  'qwen/qwen3.7-max',
] as const)

export function generationModelSupportsVision(model: GenerationModel): boolean {
  return !TEXT_ONLY_GENERATION_MODELS.has(model)
}

// A structured gradient fill (editable source of truth, serialized to the `background` CSS for render).
// The AI never emits this — the flow baker populates it from a baked gradient string so generated
// gradients open editable; it lives in the schema so the GeneratedElement type carries it.
const gradientColorStopSchema = z.object({
  id: z.string(),
  position: z.number(),
  color: z.string(),
  opacity: z.number().optional(),
})
const fillGradientSchema = z.object({
  mode: z.enum(['linear', 'radial', 'angular']),
  stops: z.array(gradientColorStopSchema),
  angle: z.number().optional(),
  position: z.object({ x: z.number(), y: z.number() }).optional(),
  radius: z.number().optional(),
  start: z.object({ x: z.number(), y: z.number() }).optional(),
  end: z.object({ x: z.number(), y: z.number() }).optional(),
})

const borderEdgeSchema = z.object({
  width: z.number(),
  color: z.string(),
  style: z.enum(['solid', 'dashed', 'dotted']).optional(),
})
const borderSidesSchema = z.object({
  top: borderEdgeSchema.optional(),
  right: borderEdgeSchema.optional(),
  bottom: borderEdgeSchema.optional(),
  left: borderEdgeSchema.optional(),
})

const shadowSpecSchema = z.object({
  x: z.number(),
  y: z.number(),
  blur: z.number(),
  spread: z.number(),
  color: z.string(),
  inset: z.boolean().optional(),
})

const generatedElementSchema = z.object({
  type: z.enum(['text', 'shape']),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  // text-only
  text: z.string().optional(),
  // Inline rich text (sanitized inline HTML) when the text had bold/italic/colored/sized runs; the
  // gradient + text-shadow preserve a generated gradient/glow heading. Only set by the flow baker.
  textHtml: z.string().optional(),
  textGradient: z.string().optional(),
  textShadow: z.string().optional(),
  textRole: z.enum(['Display', 'H1', 'H2', 'Body', 'Caption']).optional(),
  fontSize: z.number().optional(),
  fontWeight: z.number().optional(),
  lineHeight: z.number().optional(),
  color: z.string().optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  fontFamily: z.string().optional(),
  fontStyle: z.enum(['normal', 'italic']).optional(),
  letterSpacing: z.number().optional(),
  // text-only list rendering: 'bullet' → <ul>, 'number' → <ol>. Both store plain lines with no marker
  // prefix (the list element draws the bullets/numbers).
  listStyle: z.enum(['bullet', 'number']).optional(),
  // shape-only ('image' = a shape filled with a library image — see fillImage)
  shapeKind: z.enum(['rectangle', 'circle', 'image']).optional(),
  background: z.string().optional(),
  // Structured gradient fill (baker-populated, not AI-emitted) so a baked gradient stays editable.
  fillGradient: fillGradientSchema.optional(),
  strokeColor: z.string().optional(),
  strokeWidth: z.number().optional(),
  strokeStyle: z.enum(['solid', 'dashed', 'dotted']).optional(),
  // Uniform-stroke alignment (baker-populated as 'outside' when the generated element used a CSS outline;
  // a plain border stays inside). Undefined = inside (the historical default).
  strokeAlign: z.enum(['inside', 'center', 'outside']).optional(),
  // Per-side borders (baker-populated when a card's four edges differ — e.g. an accent left border or a
  // top divider). When set, overrides the uniform stroke*; absent sides render no line.
  border: borderSidesSchema.optional(),
  cornerRadius: z.number().optional(),
  // Per-corner radii [tl, tr, br, bl] when corners differ (else the scalar cornerRadius is used).
  cornerRadii: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
  boxShadow: z.string().optional(),
  // Structured box-shadow stack (baker-populated from the boxShadow string) so a baked shadow opens editable.
  boxShadows: z.array(shadowSpecSchema).optional(),
  // both: preserved from the generated CSS so the editable element matches the generating preview.
  opacity: z.number().optional(),
  rotation: z.number().optional(),
  // CSS filter / backdrop-filter (non-blur terms — brightness, saturate, frosted-glass companions)
  // preserved verbatim; the blur() term is split into the structured layerBlur / backdropBlur below.
  filter: z.string().optional(),
  backdropFilter: z.string().optional(),
  // Structured blur radii (px), baker-split from filter / backdrop-filter so they open as editable Layer /
  // Background blur effects. The renderer recomposes them with the preserved strings (utils/blur).
  layerBlur: z.number().optional(),
  backdropBlur: z.number().optional(),
  // image-only (shapeKind === 'image'): the picture URL the shape is filled with
  fillImage: z.string().optional(),
  shapeFillMode: z.enum(['color', 'image']).optional(),
  // image-only: how the picture maps onto the box — 'cover' (default, fills + crops) or 'contain'
  // (letterboxed, never cropped — used for screens/screenshots so their aspect is preserved).
  imageFit: z.enum(['cover', 'contain', 'fill']).optional(),
  // image-only: the CSS object-position the image was generated with (e.g. 'left' for an edge-pinned logo).
  imagePosition: z.string().optional(),
  // pattern-only: a tiling background (grid/lines/dots) keeps its CSS `background` string plus these so it
  // repeats across the box instead of rendering once.
  backgroundRepeat: z.string().optional(),
  backgroundSize: z.string().optional(),
})

const generatedSlideSchema = z.object({
  background: z.string().optional(),
  // Baker-populated structured gradient for the slide background (keeps a baked gradient editable).
  fillGradient: fillGradientSchema.optional(),
  elements: z.array(generatedElementSchema),
})

const generatedDeckSchema = z.object({
  slides: z.array(generatedSlideSchema).min(1),
})

export type GeneratedElement = z.infer<typeof generatedElementSchema>
export type GeneratedSlide = z.infer<typeof generatedSlideSchema>
export type GeneratedDeck = z.infer<typeof generatedDeckSchema>

export interface GenerateDeckRequest {
  prompt: string
  model: GenerationModel
}

export function isGenerationModel(value: unknown): value is GenerationModel {
  return typeof value === 'string' && (GENERATION_MODELS as readonly string[]).includes(value)
}

/**
 * Strip code fences / leading prose and return from the first `{` onward, so a partial or
 * complete model stream can be fed to a (partial) JSON parser. Used for both the streaming
 * frames and the final validate.
 */
function cleanDeckJson(text: string): string {
  let t = text.replace(/^\s*```(?:json)?/i, '').trim()
  const start = t.indexOf('{')
  if (start > 0) t = t.slice(start)
  const fenceEnd = t.lastIndexOf('```')
  if (fenceEnd !== -1) t = t.slice(0, fenceEnd)
  return t.trim()
}
